// src/pages/Subscriptions.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import Modal, { ModalForm } from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import {
    PlusIcon,
    EyeIcon,
    PencilIcon,
    XCircleIcon,
    CreditCardIcon,
    BanknotesIcon,
    CalendarIcon,
    BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const Subscriptions = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const [subscriptions, setSubscriptions] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [forceDelete, setForceDelete] = useState(false);
    const [showPricingTiers, setShowPricingTiers] = useState(false);
    const [pricingTiers, setPricingTiers] = useState([]);

    // Lazy-load the live tier configuration (public pricingTiers query) the
    // first time the panel is opened.
    const togglePricingTiers = async () => {
        const next = !showPricingTiers;
        setShowPricingTiers(next);
        if (next && pricingTiers.length === 0) {
            try {
                const data = await executeQuery(queries.getPricingTiers());
                setPricingTiers(data.pricingTiers || []);
            } catch (err) {
                console.error('Failed to load pricing tiers:', err);
            }
        }
    };
    const [filters, setFilters] = useState({
        tier: '',
        status: ''
    });
    const [formData, setFormData] = useState({
        companyId: '',
        tier: 'BASIC',
        status: 'ACTIVE',
        billingCycle: 'MONTHLY',
        price: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        trialEnd: ''
    });
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        loadSubscriptions();
        loadCompanies();
    }, [filters]);

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 1000, offset: 0 });
            setCompanies(data.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const loadSubscriptions = async () => {
        try {
            const filterVars = {
                tier: filters.tier || undefined,
                status: filters.status || undefined,
                limit: 100,
                offset: 0
            };
            const data = await executeQuery(queries.getSubscriptions(), filterVars);
            setSubscriptions(data.adminSubscriptions || []);
        } catch (err) {
            console.error('Failed to load subscriptions:', err);
        }
    };

    const handleCreateSubscription = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.createSubscription, {
                input: {
                    companyId: formData.companyId,
                    tier: formData.tier,
                    billingCycle: formData.billingCycle,
                    price: parseFloat(formData.price),
                    startDate: new Date(formData.startDate).toISOString(),
                    endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
                    trialEnd: formData.trialEnd ? new Date(formData.trialEnd).toISOString() : undefined
                }
            });

            if (result.createSubscription.success) {
                setShowCreateModal(false);
                resetForm();
                await loadSubscriptions();
            } else {
                alert('Error: ' + result.createSubscription.error);
            }
        } catch (err) {
            console.error('Failed to create subscription:', err);
            alert('Failed to create subscription: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdateSubscription = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.updateSubscription, {
                id: selectedSubscription.id,
                input: {
                    tier: formData.tier,
                    status: formData.status,
                    billingCycle: formData.billingCycle,
                    price: parseFloat(formData.price),
                    endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
                    trialEnd: formData.trialEnd ? new Date(formData.trialEnd).toISOString() : undefined
                }
            });

            if (result.updateSubscription.success) {
                setShowEditModal(false);
                setSelectedSubscription(null);
                resetForm();
                await loadSubscriptions();
            } else {
                alert('Error: ' + result.updateSubscription.error);
            }
        } catch (err) {
            console.error('Failed to update subscription:', err);
            alert('Failed to update subscription: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.cancelSubscription, {
                id: selectedSubscription.id,
                force: forceDelete
            });

            if (result.cancelSubscription.success) {
                setShowCancelModal(false);
                setSelectedSubscription(null);
                setForceDelete(false);
                await loadSubscriptions();
            } else {
                alert('Error: ' + result.cancelSubscription.error);
            }
        } catch (err) {
            console.error('Failed to cancel subscription:', err);
            alert('Failed to cancel subscription: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            companyId: '',
            tier: 'BASIC',
            status: 'ACTIVE',
            billingCycle: 'MONTHLY',
            price: 0,
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            trialEnd: ''
        });
    };

    const openDetailModal = (subscription) => {
        setSelectedSubscription(subscription);
        setShowDetailModal(true);
    };

    const openEditModal = (subscription) => {
        setSelectedSubscription(subscription);
        setFormData({
            companyId: subscription.companyId,
            tier: subscription.tier,
            status: subscription.status,
            billingCycle: subscription.billingCycle,
            price: subscription.price || 0,
            startDate: subscription.startDate ? subscription.startDate.split('T')[0] : '',
            endDate: subscription.endDate ? subscription.endDate.split('T')[0] : '',
            trialEnd: subscription.trialEnd ? subscription.trialEnd.split('T')[0] : ''
        });
        setShowEditModal(true);
    };

    const openCancelModal = (subscription) => {
        setSelectedSubscription(subscription);
        setShowCancelModal(true);
    };

    const getTierBadgeColor = (tier) => {
        switch (tier) {
            case 'BASIC': return 'info';
            case 'PRO': return 'primary';
            case 'ENTERPRISE': return 'warning';
            default: return 'default';
        }
    };

    const getTierUserLimit = (tier) => {
        switch (tier) {
            case 'BASIC': return 1;
            case 'PROFESSIONAL':
            case 'PRO': return 5;
            case 'ENTERPRISE': return 999;
            default: return 1;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const columns = [
        {
            key: 'company',
            title: 'Company',
            sortable: true,
            render: (_, subscription) => (
                <div className="flex items-center">
                    <div className="bg-primary p-2 mr-3">
                        <BuildingOfficeIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <div className="font-medium text-white">
                            {subscription.company?.name || 'Unknown Company'}
                        </div>
                        <div className="text-sm text-gray-400">
                            {subscription.company?.industry || subscription.companyId}
                        </div>
                    </div>
                </div>
            )
        },
        {
            key: 'tier',
            title: 'Tier',
            sortable: true,
            render: (tier) => (
                <StatusBadge status={tier.toLowerCase()} variant={getTierBadgeColor(tier)} />
            )
        },
        {
            key: 'teamUsage',
            title: 'Team',
            render: (_, subscription) => {
                const memberCount = subscription.company?.users?.length || 0;
                const maxUsers = subscription.company?.maxUsers || getTierUserLimit(subscription.tier);
                const defaultLimit = getTierUserLimit(subscription.tier);
                const hasOverride = subscription.company?.maxUsers && subscription.company?.maxUsers !== defaultLimit;
                const atLimit = memberCount >= maxUsers;

                return (
                    <div className="text-center">
                        <div className={`text-sm font-medium ${atLimit ? 'text-yellow-700' : 'text-white'}`}>
                            {memberCount}/{maxUsers === 999 ? '∞' : maxUsers}
                        </div>
                        {hasOverride && (
                            <div className="text-xs text-purple-700">custom</div>
                        )}
                        {atLimit && maxUsers !== 999 && (
                            <div className="text-xs text-yellow-700">at limit</div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'status',
            title: 'Status',
            sortable: true,
            render: (status) => <StatusBadge status={status} />
        },
        {
            key: 'billingCycle',
            title: 'Billing',
            render: (billingCycle, subscription) => (
                <div>
                    <div className="text-white font-medium">
                        {formatCurrency(subscription.price)}
                    </div>
                    <div className="text-sm text-gray-400">
                        per {billingCycle?.toLowerCase()}
                    </div>
                </div>
            )
        },
        {
            key: 'startDate',
            title: 'Period',
            render: (startDate, subscription) => (
                <div className="text-sm">
                    <div className="text-white">
                        {startDate ? new Date(startDate).toLocaleDateString() : '-'}
                    </div>
                    <div className="text-gray-400">
                        to {subscription.endDate ?
                        new Date(subscription.endDate).toLocaleDateString() :
                        'ongoing'
                    }
                    </div>
                </div>
            )
        },
        {
            key: 'trialEnd',
            title: 'Trial',
            render: (trialEnd) => {
                if (!trialEnd) return <span className="text-gray-400">No trial</span>;
                const isActive = new Date(trialEnd) > new Date();
                return (
                    <div className={`text-sm ${isActive ? 'text-yellow-700' : 'text-gray-400'}`}>
                        {isActive ? 'Active' : 'Ended'}
                        <div className="text-xs">
                            {new Date(trialEnd).toLocaleDateString()}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'nextBillingDate',
            title: 'Next Billing',
            render: (nextBillingDate) => nextBillingDate ? (
                <div className="text-sm">
                    <div className="text-white">{new Date(nextBillingDate).toLocaleDateString()}</div>
                </div>
            ) : (
                <span className="text-gray-400">-</span>
            )
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, subscription) => (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(subscription);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="View details"
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(subscription);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="Edit subscription"
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    {subscription.status === 'ACTIVE' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openCancelModal(subscription);
                            }}
                            className="p-1 text-gray-400 hover:text-red-700"
                            title="Cancel subscription"
                        >
                            <XCircleIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    const activeSubscriptions = subscriptions.filter(s => s.status === 'ACTIVE').length;
    const trialSubscriptions = subscriptions.filter(s => s.status === 'TRIAL').length;
    const cancelledSubscriptions = subscriptions.filter(s => s.status === 'CANCELLED').length;
    const totalRevenue = subscriptions
        .filter(s => s.status === 'ACTIVE')
        .reduce((sum, s) => sum + (s.price || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Subscriptions</h1>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={togglePricingTiers}
                        className="px-4 py-2 bg-gray-700 text-gray-200 hover:bg-gray-600 flex items-center space-x-2"
                    >
                        <BanknotesIcon className="h-4 w-4" />
                        <span>{showPricingTiers ? 'Hide Pricing' : 'Live Pricing'}</span>
                    </button>
                    <button
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                    >
                        <PlusIcon className="h-4 w-4" />
                        <span>Add Subscription</span>
                    </button>
                </div>
            </div>

            {/* Live pricing-tier configuration (what the pricing page currently offers) */}
            {showPricingTiers && (
                <div className="bg-gray-800 p-4">
                    <h3 className="font-mono text-xs font-bold text-white uppercase mb-3">Live Pricing Tiers</h3>
                    {pricingTiers.length === 0 ? (
                        <p className="text-sm text-gray-400">Loading pricing tiers…</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {pricingTiers.map(tier => (
                                <div key={tier.tier} className={`bg-gray-900 border p-4 ${tier.highlighted ? 'border-primary' : 'border-gray-700'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-white font-semibold">{tier.name}</span>
                                        <span className="font-mono text-xs text-gray-400">{tier.tier}</span>
                                    </div>
                                    <p className="text-xs text-gray-400 mb-3">{tier.description}</p>
                                    <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                                        <div><span className="text-gray-500">Monthly:</span> <span className="text-white">€{(tier.monthlyPriceCents / 100).toLocaleString('de-DE')}</span></div>
                                        <div><span className="text-gray-500">Yearly:</span> <span className="text-white">€{(tier.yearlyPriceCents / 100).toLocaleString('de-DE')}</span>{tier.yearlySavingsPercent ? <span className="text-primary"> (-{tier.yearlySavingsPercent}%)</span> : null}</div>
                                        <div><span className="text-gray-500">Trial:</span> <span className="text-white">{tier.trialDays ? `${tier.trialDays} days` : 'none'}</span></div>
                                        <div><span className="text-gray-500">Setup fee:</span> <span className="text-white">{tier.setupFeeCents ? `€${(tier.setupFeeCents / 100).toLocaleString('de-DE')}` : 'none'}</span></div>
                                        <div><span className="text-gray-500">Users:</span> <span className="text-white">{tier.maxUsers === -1 ? 'unlimited' : tier.maxUsers}</span></div>
                                        <div><span className="text-gray-500">Fingerprints:</span> <span className="text-white">{tier.maxFingerprints === -1 ? 'unlimited' : tier.maxFingerprints}</span></div>
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-2">{tier.features.length} features: {tier.features.slice(0, 6).join(', ')}{tier.features.length > 6 ? ', …' : ''}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-green-600 p-2 mr-3">
                            <CreditCardIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Active</p>
                            <p className="text-xl font-semibold text-white">{activeSubscriptions}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-yellow-600 p-2 mr-3">
                            <CalendarIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Trials</p>
                            <p className="text-xl font-semibold text-white">{trialSubscriptions}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-red-600 p-2 mr-3">
                            <XCircleIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Cancelled</p>
                            <p className="text-xl font-semibold text-white">{cancelledSubscriptions}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-primary p-2 mr-3">
                            <BanknotesIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">MRR</p>
                            <p className="text-xl font-semibold text-white">{formatCurrency(totalRevenue)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Tier</label>
                        <select
                            value={filters.tier}
                            onChange={(e) => setFilters(prev => ({ ...prev, tier: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Tiers</option>
                            <option value="BASIC">Basic</option>
                            <option value="PRO">Professional</option>
                            <option value="ENTERPRISE">Enterprise</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Status</option>
                            <option value="ACTIVE">Active</option>
                            <option value="TRIAL">Trial</option>
                            <option value="CANCELLED">Cancelled</option>
                            <option value="EXPIRED">Expired</option>
                        </select>
                    </div>
                    <div className="md:col-span-2 flex items-end">
                        <button
                            onClick={() => setFilters({ tier: '', status: '' })}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Subscriptions Table */}
            <DataTable
                columns={columns}
                data={subscriptions}
                loading={loading}
                onRowClick={(subscription) => openDetailModal(subscription)}
            />

            {/* Subscription Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedSubscription(null);
                }}
                title="Subscription Details"
                size="lg"
            >
                {selectedSubscription && <SubscriptionDetail subscription={selectedSubscription} />}
            </Modal>

            {/* Create/Edit Subscription Modals */}
            <ModalForm
                isOpen={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    resetForm();
                }}
                onSubmit={handleCreateSubscription}
                title="Create Subscription"
                submitLabel="Create Subscription"
                isLoading={submitLoading}
            >
                <SubscriptionForm formData={formData} setFormData={setFormData} companies={companies} />
            </ModalForm>

            <ModalForm
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedSubscription(null);
                    resetForm();
                }}
                onSubmit={handleUpdateSubscription}
                title="Edit Subscription"
                submitLabel="Update Subscription"
                isLoading={submitLoading}
            >
                <SubscriptionForm formData={formData} setFormData={setFormData} companies={companies} isEdit />
            </ModalForm>

            {/* Cancel Confirmation Modal */}
            <Modal
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false);
                    setSelectedSubscription(null);
                    setForceDelete(false);
                }}
                title={forceDelete ? "Delete Subscription" : "Cancel Subscription"}
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-gray-300">
                        {forceDelete
                            ? `Are you sure you want to PERMANENTLY DELETE the subscription for "${selectedSubscription?.company?.name}"? This action cannot be undone and will remove all subscription data.`
                            : `Are you sure you want to cancel the subscription for "${selectedSubscription?.company?.name}"? This will set the status to CANCELLED and end their access at the end of the current billing period.`
                        }
                    </p>

                    <label className="flex items-center space-x-3 p-3 bg-gray-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={forceDelete}
                            onChange={(e) => setForceDelete(e.target.checked)}
                            className="w-4 h-4 bg-gray-600 border-gray-500 text-red-500 focus:ring-red-500 focus:ring-offset-gray-800"
                        />
                        <div>
                            <span className="text-white font-medium">Force Delete</span>
                            <p className="text-sm text-gray-400">Permanently remove subscription from database (cannot be undone)</p>
                        </div>
                    </label>

                    {forceDelete && (
                        <div className="p-3 bg-red-50 border border-red-200">
                            <p className="text-red-700 text-sm">
                                Warning: This will permanently delete the subscription record. Use this only if you want to completely remove this subscription from the system.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => {
                                setShowCancelModal(false);
                                setSelectedSubscription(null);
                                setForceDelete(false);
                            }}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCancelSubscription}
                            disabled={submitLoading}
                            className={`px-4 py-2 text-white ${
 forceDelete
 ? 'bg-red-600 hover:bg-red-500'
 : 'bg-yellow-600 hover:bg-yellow-500'
 } disabled:opacity-50`}
                        >
                            {submitLoading ? 'Processing...' : (forceDelete ? 'Delete Subscription' : 'Cancel Subscription')}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

// Subscription Detail Component
const SubscriptionDetail = ({ subscription }) => {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    return (
        <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Subscription Details</h4>
                    <div className="space-y-2 text-sm">
                        <div>
                            <span className="text-gray-400">Company:</span>
                            <span className="text-white ml-2">{subscription.company?.name || 'Unknown'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Tier:</span>
                            <span className="ml-2">
                                <StatusBadge status={subscription.tier.toLowerCase()} />
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Status:</span>
                            <span className="ml-2">
                                <StatusBadge status={subscription.status} />
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Price:</span>
                            <span className="text-white ml-2">
                                {formatCurrency(subscription.price)} / {subscription.billingCycle?.toLowerCase()}
                            </span>
                        </div>
                        {subscription.priceWithDiscount !== subscription.price && (
                            <div>
                                <span className="text-gray-400">Discounted Price:</span>
                                <span className="text-green-700 ml-2">
                                    {formatCurrency(subscription.priceWithDiscount)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Timeline</h4>
                    <div className="space-y-2 text-sm">
                        <div>
                            <span className="text-gray-400">Start Date:</span>
                            <span className="text-white ml-2">
                                {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : '-'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">End Date:</span>
                            <span className="text-white ml-2">
                                {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'Ongoing'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Trial End:</span>
                            <span className="text-white ml-2">
                                {subscription.trialEnd ? new Date(subscription.trialEnd).toLocaleDateString() : 'No trial'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Next Billing:</span>
                            <span className="text-white ml-2">
                                {subscription.nextBillingDate ?
                                    new Date(subscription.nextBillingDate).toLocaleDateString() :
                                    'Not scheduled'
                                }
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Billing History */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Billing Information</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Payment Method:</span>
                            <span className="text-white ml-2">{subscription.paymentMethod || 'Not set'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Last Billing:</span>
                            <span className="text-white ml-2">
                                {subscription.lastBillingDate ?
                                    new Date(subscription.lastBillingDate).toLocaleDateString() :
                                    'Never'
                                }
                            </span>
                        </div>
                        {subscription.canceledAt && (
                            <div>
                                <span className="text-gray-400">Cancelled At:</span>
                                <span className="text-red-700 ml-2">
                                    {new Date(subscription.canceledAt).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Team Usage */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Team Usage</h4>
                <div className="bg-gray-700 p-4">
                    {(() => {
                        const getTierUserLimit = (tier) => {
                            switch (tier) {
                                case 'BASIC': return 1;
                                case 'PROFESSIONAL':
                                case 'PRO': return 5;
                                case 'ENTERPRISE': return 999;
                                default: return 1;
                            }
                        };
                        const memberCount = subscription.company?.users?.length || 0;
                        const defaultLimit = getTierUserLimit(subscription.tier);
                        const maxUsers = subscription.company?.maxUsers || defaultLimit;
                        const hasOverride = subscription.company?.maxUsers && subscription.company?.maxUsers !== defaultLimit;
                        const atLimit = memberCount >= maxUsers;

                        return (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-400">Tier Default:</span>
                                        <span className="text-white ml-2">
                                            {defaultLimit === 999 ? 'Unlimited' : `${defaultLimit} users`}
                                        </span>
                                    </div>
                                    {hasOverride && (
                                        <div>
                                            <span className="text-gray-400">Override:</span>
                                            <span className="text-purple-700 ml-2">
                                                {maxUsers === 999 ? 'Unlimited' : `${maxUsers} users`} (admin set)
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-sm">
                                        <span className="text-gray-400">Current Usage:</span>
                                        <span className={`ml-2 font-medium ${atLimit ? 'text-yellow-700' : 'text-white'}`}>
                                            {memberCount} / {maxUsers === 999 ? '∞' : maxUsers}
                                        </span>
                                        {atLimit && maxUsers !== 999 && (
                                            <span className="ml-2 text-xs text-yellow-700">(at limit)</span>
                                        )}
                                    </div>
                                </div>
                                {/* Progress bar */}
                                {maxUsers !== 999 && (
                                    <div className="w-full bg-gray-600 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full ${
 atLimit
 ? 'bg-red-500'
 : memberCount / maxUsers > 0.8
 ? 'bg-yellow-500'
 : 'bg-primary'
 }`}
                                            style={{ width: `${Math.min(100, (memberCount / maxUsers) * 100)}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Modules */}
            {subscription.modules && subscription.modules.length > 0 && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Add-on Modules</h4>
                    <div className="bg-gray-700 p-4">
                        <div className="space-y-2">
                            {subscription.modules.map((module) => (
                                <div key={module.id} className="flex justify-between items-center text-sm">
                                    <span className="text-white">{module.type}</span>
                                    <span className="text-gray-400">{formatCurrency(module.price)}/month</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Discounts */}
            {subscription.discounts && subscription.discounts.length > 0 && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Applied Discounts</h4>
                    <div className="bg-gray-700 p-4">
                        <div className="space-y-2">
                            {subscription.discounts.map((discount) => (
                                <div key={discount.id} className="text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-white">{discount.code}</span>
                                        <span className="text-green-700">
                                            {discount.type === 'PERCENTAGE' ?
                                                `${discount.value}%` :
                                                formatCurrency(discount.value)
                                            }
                                        </span>
                                    </div>
                                    <div className="text-gray-400">{discount.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Metadata */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Metadata</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="text-gray-400">Created:</span>
                        <span className="text-white ml-2">{new Date(subscription.createdAt).toLocaleString()}</span>
                    </div>
                    <div>
                        <span className="text-gray-400">Updated:</span>
                        <span className="text-white ml-2">{new Date(subscription.updatedAt).toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Subscription Form Component
const SubscriptionForm = ({ formData, setFormData, companies = [], isEdit = false }) => {
    // Filter out companies that already have subscriptions (for create mode)
    const availableCompanies = companies;

    return (
        <div className="space-y-4">
            {!isEdit && (
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Company *</label>
                    <select
                        value={formData.companyId}
                        onChange={(e) => setFormData(prev => ({ ...prev, companyId: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    >
                        <option value="">Select a company...</option>
                        {availableCompanies.map(company => (
                            <option key={company.id} value={company.id}>
                                {company.name} {company.industry ? `(${company.industry})` : ''} {company.subscription ? '- Has subscription' : ''}
                            </option>
                        ))}
                    </select>
                    {formData.companyId && (
                        <p className="text-xs text-gray-500 mt-1">ID: {formData.companyId}</p>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Tier *</label>
                    <select
                        value={formData.tier}
                        onChange={(e) => setFormData(prev => ({ ...prev, tier: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    >
                        <option value="BASIC">Basic</option>
                        <option value="PRO">Professional</option>
                        <option value="ENTERPRISE">Enterprise</option>
                    </select>
                </div>

                {isEdit && (
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Status *</label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            required
                        >
                            <option value="ACTIVE">Active</option>
                            <option value="TRIAL">Trial</option>
                            <option value="CANCELLED">Cancelled</option>
                            <option value="EXPIRED">Expired</option>
                        </select>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Billing Cycle *</label>
                    <select
                        value={formData.billingCycle}
                        onChange={(e) => setFormData(prev => ({ ...prev, billingCycle: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    >
                        <option value="MONTHLY">Monthly</option>
                        <option value="YEARLY">Yearly</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Price *</label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                        placeholder="0.00"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Start Date *</label>
                    <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">End Date</label>
                    <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="Leave empty for ongoing"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Trial End Date</label>
                <input
                    type="date"
                    value={formData.trialEnd}
                    onChange={(e) => setFormData(prev => ({ ...prev, trialEnd: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    placeholder="Leave empty if no trial"
                />
                <p className="text-xs text-gray-400 mt-1">Leave empty if this subscription doesn&apos;t have a trial period</p>
            </div>
        </div>
    );
};

export default Subscriptions;